import {
    Alert,
    Box,
    Stack,
    FormControl,
    Grid,
    InputLabel,
    Typography,
    OutlinedInput,
    InputAdornment,
    IconButton,
    Snackbar,
    Tab,
    Tabs,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { RootState } from '../../../../reducers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import { fetchActiveStatusMembers, fetchMemberStatuses } from '../../../../store/members.slice';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import {
    setSelectedMember,
    patchMapToMember,
    clearSelections,
    setShowArchivedAlerts,
    closeConfirmationModalHandler,
    setTeamNotificationModalOpen,
    setConfirmationModalOpen,
    deleteMultipleAlertsHandler,
    setOpenHelper,
    handleOpenHelper,
    setCurrentAlertTab,
    archiveMultipleAlertsHandler,
    getADTAlerts,
    getMIHAAlerts,
    getOutpatientAlerts,
    getMCAlerts,
    getLabAlerts,
    getFuhFumAlerts,
    setAlertsList,
    getHedisLabAlerts,
    fetchMemberCareTeam,
    setMemberCareTeam,
} from '../../../../store/hie.slice';
import Button from '@mui/material/Button';
import { Search } from '@mui/icons-material';
import Authorization from '../../../../utils/Authorization';
import ConfirmationModal from '../../Modals/ConfirmationModal';
import TeamNotificationModal from '../../Modals/TeamNotificationModal';
import ADTAlerts from './AlertTypes/ADTAlerts/ADTAlerts';
import MIHAAlerts from './AlertTypes/MIHAAlerts/MIHAAlerts';

import MedicaidCensus from './AlertTypes/MedicaidCensus/MedicaidCensus';
import { apiString as API_URL, parentUrl } from '../../../../utils/constants';
import axios from 'axios';
import { setIsLoading, fetchRoles } from '../../../../store/shared.slice';
import SelectedMember from '../../SelectedMember/SelectedMember';
import OutpatientAlerts from './AlertTypes/OutpatientAlerts/OutpatientAlerts';
import LabAlerts from './AlertTypes/LabAlerts/LabAlerts';
import FuhFumOpenAlerts from './AlertTypes/FuhFum/FuhFumOpen';
import Loading from '../../../Shared/Loading';
import HedisLabAlerts from './AlertTypes/HedisLabAlerts/HedisLabAlerts';

const Alerts = () => {
    const auth = new Authorization();
    const dispatch = useAppDispatch();
    const { members } = useSelector((state: RootState) => state.members);
    const history = useNavigate();

    const {
        adtAlerts,
        alertsList,
        confirmationModalAction,
        confirmationModalMessage,
        confirmationModalOpen,
        currentAlertTab,
        fuhfumAlerts,
        hedisLabAlerts,
        helperMessage,
        labAlerts,
        mcAlerts,
        memberCareTeam,
        mihaAlerts,
        openHelper,
        outpatientAlerts,
        rowSelectionModel,
        selectedMember,
        showArchivedAlerts,
        teamNotificationModalOpen,
        top3Matches,
    } = useSelector((state: RootState) => state.hie);

    const { roles } = useSelector((state: RootState) => state.shared);

    const [memberSearchValue, setMemberSearchValue] = useState('');
    const [allMembers, setAllMembers] = useState([]);
    const [memberSearchTab, setMemberSearchTab] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

    const HIETabs = [
        { label: 'ADT Alerts', value: { list: adtAlerts, callback: getADTAlerts } },
        { label: 'MIHA Alerts', value: { list: mihaAlerts, callback: getMIHAAlerts } },
        { label: 'Outpatient Alerts', value: { list: outpatientAlerts, callback: getOutpatientAlerts } },
        { label: 'Medicaid Census', value: { list: mcAlerts, callback: getMCAlerts } },
        { label: 'HEDIS Lab Alerts', value: { list: hedisLabAlerts, callback: getHedisLabAlerts } },
        { label: 'Lab Alerts', value: { list: labAlerts, callback: getLabAlerts } },
        { label: 'FUHFUM Open Status', value: { list: fuhfumAlerts, callback: getFuhFumAlerts } },
    ];

    const currentTab = HIETabs.find((tab) => tab.label === currentAlertTab)?.value;

    const handleCloseHelper = () => {
        dispatch(setOpenHelper(false));
    };

    const [fileIsValid, setFileIsValid] = useState(true);
    const uploadMedicaidCensusHandler = async (e) => {
        let formData = new FormData();
        let file = e.target.files[0];

        formData.append('File', file);
        dispatch(setIsLoading(true));
        try {
            const response = await axios.put(`${API_URL}/HIE/addmedicaidfiletodatabase`, formData);
            if (response.status === 200) {
                dispatch(getMCAlerts());
            }
            dispatch(setIsLoading(false));
        } catch (error) {
            console.log('ERROR: ', error);
            dispatch(setIsLoading(false));
        }
        setFileIsValid(true);
    };

    const closeTeamNotificationModalHandler = () => {
        if (!selectedMember?.Id) {
            console.error('Cannot match alerts: no member selected');
            dispatch(setTeamNotificationModalOpen(false));
            return;
        }

        const endpoint =
            currentAlertTab === 'Outpatient Alerts' || currentAlertTab === 'Lab Alerts' || currentAlertTab === 'HEDIS Lab Alerts'
                ? 'member-documents'
                : 'transitional-care';
        const url = `${parentUrl}provider/members-list/member-chart/${selectedMember.Id}/${endpoint}`;

        dispatch(patchMapToMember(selectedMember.Id, auth.UserName, rowSelectionModel, currentTab.callback, url));
        dispatch(setTeamNotificationModalOpen(false));
    };

    const handleMemberSearchTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        dispatch(clearSelections());
        setMemberSearchTab(newValue);
        if (newValue === 1) {
            setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
        }
    };

    const handleAlertsTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
        dispatch(clearSelections());
        dispatch(setCurrentAlertTab(newValue));
    };

    const matchSelectedMemberHandler = () => {
        if (selectedMember === null && rowSelectionModel.length === 0) {
            dispatch(handleOpenHelper('No Alert or CHP Member selected for matching. Select an Alert(s) and CHP Member to match.'));
        } else if (rowSelectionModel.length === 0) {
            dispatch(handleOpenHelper('No alerts selected for matching. Click the checkbox next to the alert(s) you wish to match.'));
        } else if (selectedMember === null) {
            dispatch(handleOpenHelper('No CHP Member selected for matching. Click a row to get Top 3 Matches or Search All Members.'));
        } else {
            dispatch(setAlertsList(currentTab.list.filter((el) => rowSelectionModel.includes(el.Id))));
            dispatch(setTeamNotificationModalOpen(true));
        }
    };

    const executeSearch = (event: any) => {
        if (!event.key || event.key === 'Enter') {
            var tmp = [];
            const searchValue = memberSearchValue.toLowerCase();
            for (const member of members) {
                if (
                    (member.LastName && member.LastName.toLowerCase().indexOf(searchValue) > -1) ||
                    (member.FirstName && member.FirstName.toLowerCase().indexOf(searchValue) > -1)
                ) {
                    tmp.push(member);
                }
            }
            setAllMembers(tmp);
            setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
        }
    };

    useEffect(() => {
        dispatch(fetchActiveStatusMembers());
        dispatch(fetchMemberStatuses());
        dispatch(fetchRoles());
    }, [dispatch]);

    useEffect(() => {
        if (members.length > 0) {
            setAllMembers(members);
        }
    }, [members]);

    useEffect(() => {
        if (selectedMember?.Id) {
            dispatch(fetchMemberCareTeam(selectedMember.Id));
        } else {
            dispatch(setMemberCareTeam(null));
        }
    }, [selectedMember, dispatch]);

    const top3MatchesColumns: GridColDef[] = [
        {
            field: 'LastName',
            headerName: 'Last Name',

            flex: 1,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',

            flex: 1,
        },
    ];

    const navigateToFuhFum = () => {
        history(`fuhfum`);
    };

    return (
        <>
            {<Loading message={'Loading...'} />}
            <ConfirmationModal
                confirmationModalMessage={confirmationModalMessage}
                handleClose={() => dispatch(closeConfirmationModalHandler(currentTab.callback))}
                handleCancel={() => dispatch(setConfirmationModalOpen(false))}
                open={confirmationModalOpen}
                handleAction={confirmationModalAction}
                alertsList={alertsList}
            />
            <TeamNotificationModal
                selectedMember={selectedMember}
                memberCareTeam={memberCareTeam}
                roles={roles}
                handleMatch={closeTeamNotificationModalHandler}
                handleCancel={() => dispatch(setTeamNotificationModalOpen(false))}
                open={teamNotificationModalOpen}
                alertsList={alertsList}
            />
            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={openHelper} autoHideDuration={6000} onClose={handleCloseHelper}>
                <Alert sx={{ width: '100%' }} variant="filled" elevation={6} severity="error" onClose={handleCloseHelper}>
                    {helperMessage}
                </Alert>
            </Snackbar>
            <Box>
                <Grid container paddingBottom={0}>
                    <Stack direction={'row'} width="100%" spacing={4} paddingX={2} paddingTop={2} sx={{ zIndex: 10, background: '#ffffff' }}>
                        <Grid size={3}>
                            <Tabs variant="fullWidth" value={memberSearchTab} onChange={handleMemberSearchTabChange}>
                                <Tab value={0} label="Top 3 Matches" />
                                <Tab value={1} label="Search All Members" />
                            </Tabs>
                            {memberSearchTab === 0 && (
                                <div style={{ width: '100%', marginTop: '16px' }}>
                                    <DataGrid
                                        onCellClick={(params: any) => {
                                            dispatch(setSelectedMember(params.row));
                                        }}
                                        autoHeight
                                        rows={top3Matches}
                                        columns={top3MatchesColumns}
                                        paginationModel={{ pageSize: 3, page: 0 }}
                                        getRowId={(row) => row.Id}
                                        sx={{
                                            [`& .${gridClasses.cell}`]: {
                                                py: 1,
                                            },
                                        }}
                                    />
                                </div>
                            )}
                            {memberSearchTab === 1 && (
                                <>
                                    <FormControl fullWidth style={{ marginTop: '16px', marginBottom: '16px' }}>
                                        <InputLabel htmlFor="outlined-adornment-password">Search</InputLabel>
                                        <OutlinedInput
                                            id="outlined-adornment-password"
                                            type="text"
                                            value={memberSearchValue}
                                            onChange={(e) => {
                                                setMemberSearchValue(e.target.value as string);
                                            }}
                                            onKeyUp={(event: any) => {
                                                executeSearch(event);
                                            }}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton aria-label="Search" onClick={executeSearch} edge="end">
                                                        <Search />
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            label="Search"
                                        />
                                    </FormControl>
                                    <div style={{ width: '100%' }}>
                                        <DataGrid
                                            onCellClick={(params: any) => {
                                                dispatch(setSelectedMember(params.row));
                                            }}
                                            autoHeight
                                            rows={allMembers}
                                            columns={top3MatchesColumns}
                                            paginationModel={paginationModel}
                                            onPaginationModelChange={setPaginationModel}
                                            pageSizeOptions={[5, 10, 25, 50]}
                                            getRowId={(row) => row.Id}
                                            sx={{
                                                [`& .${gridClasses.cell}`]: {
                                                    py: 1,
                                                },
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                            <Button fullWidth className="btn-120" variant="contained" onClick={matchSelectedMemberHandler} sx={{ marginTop: 2 }}>
                                Match Member to Alert&#40;s&#41;
                            </Button>
                        </Grid>

                        <SelectedMember selectedMember={selectedMember} />

                        <Grid size={2} textAlign="right">
                            <Stack spacing={2} alignItems={'center'}>
                                <form id={'upload-form'}>
                                    <Button size="small" color="success" variant="contained" id="upload-btn" component="label" sx={{ textAlign: 'center' }}>
                                        Upload Medicaid Census
                                        <input
                                            id="file"
                                            name="File"
                                            onChange={(e) => {
                                                uploadMedicaidCensusHandler(e);
                                            }}
                                            type="file"
                                            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                            hidden
                                        />
                                    </Button>
                                </form>
                                {!fileIsValid && (
                                    <Typography variant="h6" color="error">
                                        Invalid File
                                    </Typography>
                                )}
                            </Stack>
                            <Stack spacing={2} paddingTop={2} alignItems={'center'}>
                                <Button
                                    size="small"
                                    color="primary"
                                    variant="contained"
                                    component="label"
                                    sx={{ textAlign: 'center' }}
                                    onClick={navigateToFuhFum}
                                >
                                    UPLOAD FUHFUM
                                </Button>
                            </Stack>
                        </Grid>
                    </Stack>

                    <Grid size={12} display={'flex'} justifyContent={'flex-end'} p={2}>
                        <Button
                            className="btn-120"
                            variant="contained"
                            onClick={() => dispatch(archiveMultipleAlertsHandler(currentTab))}
                            sx={{ marginRight: 2 }}
                        >
                            Archive Alert&#40;s&#41;
                        </Button>
                        <Button
                            className="btn-120"
                            color="error"
                            variant="contained"
                            onClick={() => dispatch(deleteMultipleAlertsHandler(currentTab))}
                            sx={{ marginRight: 2 }}
                        >
                            Delete Alert&#40;s&#41;
                        </Button>
                    </Grid>
                    <Grid size={12} display={'flex'}>
                        <Tabs
                            sx={{ marginTop: 2, display: 'inline-flex', marginRight: 'auto' }}
                            value={currentAlertTab}
                            onChange={handleAlertsTabChange}
                            variant="scrollable"
                        >
                            {HIETabs.map((tab, index) => {
                                return <Tab key={`${tab.label}_${index}`} value={tab.label} label={tab.label} />;
                            })}
                        </Tabs>
                        <FormControlLabel
                            control={<Checkbox checked={showArchivedAlerts} onChange={() => dispatch(setShowArchivedAlerts(!showArchivedAlerts))} />}
                            label="Include Archived Alerts"
                        />
                    </Grid>
                    <Grid size={12} p={2}>
                        <div style={{ width: '100%', height: '73vh' }}>
                            {currentAlertTab === 'ADT Alerts' && <ADTAlerts />}
                            {currentAlertTab === 'MIHA Alerts' && <MIHAAlerts />}
                            {currentAlertTab === 'Outpatient Alerts' && <OutpatientAlerts />}
                            {currentAlertTab === 'Medicaid Census' && <MedicaidCensus />}
                            {currentAlertTab === 'HEDIS Lab Alerts' && <HedisLabAlerts />}
                            {currentAlertTab === 'Lab Alerts' && <LabAlerts />}
                            {currentAlertTab === 'FUHFUM Open Status' && <FuhFumOpenAlerts />}
                        </div>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default Alerts;
